import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useState } from "react";
import { useProfileStore } from "./ProfileData";
import { SkillsHeader } from "./skills/SkillsHeader";
import { SkillsAddForm } from "./skills/SkillsAddForm";
import { SkillsSearch } from "./skills/SkillsSearch";
import { SkillsList } from "./skills/SkillsList";
import { BadgeCollection } from "./badges/BadgeCollection";
import { ClientAppreciationBadges } from "./badges/ClientAppreciationBadges";
import { BadgeProgress } from "./badges/BadgeProgress";
import { Separator } from "@/components/ui/separator";
import { ProfileBadge } from "./badges/BadgeDisplay";
import { Award, Heart, Trophy, Star, Users, Gem } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFreelancerStore } from "@/store/freelancerStore";
import { useToast } from "@/hooks/use-toast";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

// Define the ProfileBadgeType to include "achievement"
type ProfileBadgeType =
  | "skill-expert"
  | "top-rated"
  | "rising-talent"
  | "project-completion"
  | "client-favorite"
  | "client-appreciation"
  | "elite-talent"
  | "community-contributor"
  | "quality-champion"
  | "achievement";

// Sample badges data - in a real app, this would come from the API or store
// const sampleBadges: ProfileBadge[] = [
//   {
//     id: "1",
//     type: "skill-expert",
//     name: "React Expert",
//     description: "Recognized for exceptional expertise in React development with 10+ successfully completed projects.",
//     earnedDate: "2023-05-15",
//     level: 3,
//     rarity: "rare"
//   },
//   {
//     id: "2",
//     type: "top-rated",
//     name: "Top Rated",
//     description: "Maintained a perfect 5-star rating across all client evaluations.",
//     earnedDate: "2023-06-22",
//     level: 2,
//     rarity: "uncommon"
//   },
//   {
//     id: "3",
//     type: "rising-talent",
//     name: "Rising Talent",
//     description: "Identified as an up-and-coming talent in the platform community.",
//     earnedDate: "2023-03-10",
//     level: 1,
//     rarity: "common"
//   },
//   {
//     id: "4",
//     type: "project-completion",
//     name: "Project Master",
//     description: "Successfully completed over 25 projects with excellent client feedback.",
//     earnedDate: "2023-07-05",
//     level: 2,
//     rarity: "uncommon"
//   },
//   {
//     id: "5",
//     type: "client-favorite",
//     name: "Client Favorite",
//     description: "Consistently chosen by repeat clients for follow-up projects.",
//     earnedDate: "2023-08-30",
//     level: 1,
//     rarity: "common"
//   },
//   // New enhanced badges
//   {
//     id: "6",
//     type: "client-appreciation",
//     name: "Client Gratitude",
//     description: "Awarded for going above and beyond client expectations.",
//     earnedDate: "2023-09-12",
//     clientName: "Sarah Johnson",
//     clientMessage: "Alex's work exceeded our expectations. Not only did they deliver ahead of schedule, but they also added extra features we hadn't thought of!",
//     rarity: "uncommon"
//   },
//   {
//     id: "7",
//     type: "elite-talent",
//     name: "Elite Developer",
//     description: "Recognized as being in the top 1% of developers on the platform.",
//     earnedDate: "2023-10-05",
//     rarity: "legendary"
//   },
//   {
//     id: "8",
//     type: "community-contributor",
//     name: "Community Leader",
//     description: "Contributed significantly to the developer community through mentorship and knowledge sharing.",
//     earnedDate: "2023-11-20",
//     rarity: "rare"
//   },
//   {
//     id: "9",
//     type: "client-appreciation",
//     name: "Excellence Award",
//     description: "Awarded for delivering exceptional quality work that transformed the client's business.",
//     earnedDate: "2023-12-15",
//     clientName: "Michael Roberts",
//     clientMessage: "Working with Alex was incredible. Their attention to detail and creative problem-solving skills saved our project from major issues.",
//     rarity: "rare"
//   },
//   {
//     id: "10",
//     type: "quality-champion",
//     name: "Quality Champion",
//     description: "Consistently delivers code with zero defects and exceptional documentation.",
//     earnedDate: "2024-01-10",
//     rarity: "epic"
//   }
// ];

// Skill certification badges
// const certificationBadges: ProfileBadge[] = [
//   {
//     id: "c1",
//     type: "skill-expert",
//     name: "Certified TypeScript",
//     description: "Completed official TypeScript certification with a score in the top 10%.",
//     earnedDate: "2023-04-18",
//     level: 3,
//     rarity: "rare"
//   },
//   {
//     id: "c2",
//     type: "skill-expert",
//     name: "AWS Solutions Architect",
//     description: "Certified AWS Solutions Architect - Associate level.",
//     earnedDate: "2023-02-07",
//     level: 2,
//     rarity: "uncommon"
//   },
//   {
//     id: "c3",
//     type: "skill-expert",
//     name: "React Advanced Developer",
//     description: "Mastery of React ecosystem including Redux, Next.js, and React Native.",
//     earnedDate: "2024-02-15",
//     level: 3,
//     rarity: "epic"
//   }
// ];

// Client appreciation badges
// const clientBadges: ProfileBadge[] = [
//   {
//     id: "ca1",
//     type: "client-appreciation",
//     name: "Client Hero",
//     description: "Went above and beyond to solve critical issues for the client.",
//     earnedDate: "2023-11-05",
//     clientName: "Emma Wilson",
//     clientMessage: "Alex saved our product launch by working through the weekend to fix critical bugs that our internal team couldn't solve.",
//     rarity: "rare"
//   },
//   {
//     id: "ca2",
//     type: "client-appreciation",
//     name: "Exceptional Service",
//     description: "Provided exceptional service and support throughout the project.",
//     earnedDate: "2024-01-22",
//     clientName: "David Chen",
//     clientMessage: "Not only was the work excellent, but the level of communication and support was outstanding. Alex made sure we understood every aspect of the implementation.",
//     rarity: "uncommon"
//   },
//   {
//     id: "ca3",
//     type: "client-appreciation",
//     name: "Innovative Solution",
//     description: "Created an innovative solution that exceeded client expectations.",
//     earnedDate: "2024-03-10",
//     clientName: "Jessica Miller",
//     clientMessage: "The solution Alex developed was so innovative that it helped us secure additional funding for our startup. Truly game-changing work!",
//     rarity: "epic"
//   },
//   {
//     id: "ca4",
//     type: "client-appreciation",
//     name: "Reliability Star",
//     description: "Consistently reliable and dependable throughout long-term projects.",
//     earnedDate: "2024-02-05",
//     clientName: "Robert Taylor",
//     clientMessage: "Over our 8-month project, Alex never missed a deadline and was always available when we needed assistance. A true professional.",
//     rarity: "uncommon"
//   }
// ];

interface ProfileSkillsProps {
  formData: {
    bio: string;
    _id: string;
    skills: string[];
    certifications: string[];
    achievements?: string[];
  };
  accessToken: string | null;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  freelancerId?: string | null;
}

export const ProfileSkills = ({
  formData,
  accessToken,
  setFormData,
  freelancerId
}: ProfileSkillsProps) => {
  const { profile, updateFreelancerProfile } =
    useFreelancerStore();
  const profileData = profile;
  const { toast } = useToast();
  const [isAdding, setIsAdding] = useState(false);
  const [newSkill, setNewSkill] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [badgeTab, setBadgeTab] = useState("achievements");
  const [newAchievement, setNewAchievement] = useState("");
  const [newCertification, setNewCertification] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  
  const generateId = (prefix: string, index: number) =>
    `${prefix}-${index}-${Date.now()}`;

  const handleAddSkill = async () => {
    if (newSkill.trim()) {
      const newSkills = newSkill
        .split(",")
        .map((skill) => skill.trim())
        .filter(
          (skill) => skill.length > 0 && !profileData?.skills.includes(skill)
        );

      if (newSkills.length > 0) {
        const updatedSkills = [...profileData?.skills, ...newSkills];
        await updateFreelancerProfile(
          formData._id,
          { skills: updatedSkills },
          accessToken
        );

        toast({
          title: "Profile Updated",
          description: `Added ${newSkills.join(", ")} to skills`,
          variant: "success",
        });

        setNewSkill("");
        setIsAdding(false);
      }
    }
  };

  const handleAddAchievement = async () => {
    if (newAchievement.trim()) {
      const updatedAchievements = [
        ...(formData.achievements || []),
        newAchievement.trim(),
      ];
      try {
        await updateFreelancerProfile(
          formData._id,
          { achievements: updatedAchievements },
          accessToken
        );
        setFormData((prev: any) => ({
          ...prev,
          achievements: updatedAchievements,
        }));
        toast({
          title: "Achievement Added",
          description: `Added "${newAchievement}" to achievements`,
          variant: "success",
        });
        setNewAchievement("");
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to add achievement. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const handleRemoveAchievement = async (name: string) => {
    const updateName = name?.charAt(0).toLowerCase() + name?.slice(1);
    const updatedAchievements = (formData.achievements || []).filter(
      (ach) => ach !== updateName
    );
    try {
      await updateFreelancerProfile(
        formData._id,
        { achievements: updatedAchievements },
        accessToken
      );
      setFormData((prev: any) => ({
        ...prev,
        achievements: updatedAchievements,
      }));
      toast({
        title: "Achievement Removed",
        description: `Removed "${name}" from achievements`,
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove achievement. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAddCertification = async () => {
    if (newCertification.trim()) {
      const updatedCertifications = [
        ...(formData.certifications || []),
        newCertification.trim(),
      ];
      try {
        await updateFreelancerProfile(
          formData._id,
          { certifications: updatedCertifications },
          accessToken
        );
        setFormData((prev: any) => ({
          ...prev,
          certifications: updatedCertifications,
        }));
        toast({
          title: "Certification Added",
          description: `Added "${newCertification}" to certifications`,
          variant: "success",
        });
        setNewCertification("");
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to add certification. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const handleRemoveCertification = async (name: string) => {
    const updatedCertifications = (formData.certifications || []).filter(
      (cert) => cert !== name
    );
    try {
      await updateFreelancerProfile(
        formData._id,
        { certifications: updatedCertifications },
        accessToken
      );
      setFormData((prev: any) => ({
        ...prev,
        certifications: updatedCertifications,
      }));
      toast({
        title: "Certification Removed",
        description: `Removed "${name}" from certifications`,
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove certification. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveSkill = async (skillToRemove: string) => {
    const updatedSkills = profileData?.skills.filter(
      (skill) => skill !== skillToRemove
    );
    await updateFreelancerProfile(
      formData._id,
      { skills: updatedSkills },
      accessToken
    );
    toast({
      title: "Profile Updated",
      description: `Removed ${skillToRemove.trim()} from skills`,
      variant: "success",
    });
  };

  // Map achievements to badges
  const achievementBadges: ProfileBadge[] =
    formData?.achievements?.length > 0
      ? formData?.achievements?.map((ach, index) => {
          return {
            id: generateId("ach", index),
            type: "skill-expert",
            name: ach.charAt(0).toUpperCase() + ach.slice(1),
            description: `Recognized for ${ach}, showcasing excellence and accomplishment.`,
            level: 3,
            rarity: "rare",
            earnedDate: new Date().toISOString().split("T")[0],
          };
        })
      : [];

  // Map certifications to badges
  const certificationBadges: ProfileBadge[] =
    formData?.certifications?.length > 0
      ? formData?.certifications?.map((cert, index) => ({
          id: generateId("cert", index),
          type: "skill-expert",
          name: cert,
          description: `Certified in ${cert}, demonstrating proficiency in related technologies and practices.`,
          level: 2,
          rarity: "uncommon",
          earnedDate: new Date().toISOString().split("T")[0],
        }))
      : [];

  const filteredSkills = searchQuery
    ? profileData?.skills.filter((skill) =>
        skill.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : profileData?.skills;

  return (
    <Card className="hover:shadow-md transition-all duration-300 bg-gradient-to-br from-white to-blue-50/30 border-blue-100">
      <CardHeader className="pb-2">
        <SkillsHeader setIsAdding={setIsAdding} isAdding={isAdding} freelancerId={freelancerId}/>

        {(isAdding || (filteredSkills && filteredSkills?.length > 5)) && (
          <div className="mt-4 flex gap-2 animate-fade-in">
            {isAdding && !freelancerId && (
              <div className="flex-1 bg-gradient-to-r from-blue-50 to-emerald-50 p-3 rounded-lg border border-blue-100 shadow-sm">
                <h4 className="text-sm font-medium mb-2 text-blue-700">
                  Add New Skill
                </h4>
                <SkillsAddForm
                  newSkill={newSkill}
                  setNewSkill={setNewSkill}
                  handleAddSkill={handleAddSkill}
                />
              </div>
            )}

            {!isAdding && filteredSkills.length > 5 && (
              <div className="flex-1">
                <SkillsSearch
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                />
              </div>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <SkillsList
          filteredSkills={filteredSkills}
          handleRemoveSkill={handleRemoveSkill}
          searchQuery={searchQuery}
        />

        {/* Achievements and Badges Section */}
        <div
          className="mt-8 space-y-6 animate-fade-in"
          style={{ animationDelay: "0.2s" }}
        >
          <div>
            <h3 className="text-lg font-medium bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4 flex items-center gap-2">
              <Award className="h-5 w-5 text-blue-500" />
              Achievements & Recognition
            </h3>
            <Separator className="mb-5 bg-gradient-to-r from-blue-100 to-purple-100" />

            {/* Client Appreciation Showcase */}
            {/* <div className="mb-8">
              <ClientAppreciationBadges badges={clientBadges} />
            </div> */}

            {/* Badge Progress Section */}
            {/* <div className="mb-8 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 p-4 rounded-lg border border-blue-100">
              <h4 className="text-sm font-medium text-indigo-700 mb-3">Progress Towards Next Achievements</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <BadgeProgress 
                  badgeType="top-rated"
                  badgeName="Ultimate Professional"
                  description="Complete 50 projects with a 5-star rating to unlock this prestigious badge."
                  currentValue={35}
                  maxValue={50}
                  unlocked={false}
                />
                <BadgeProgress 
                  badgeType="elite-talent"
                  badgeName="Platform Champion"
                  description="Reach the top 1% of freelancers in your category."
                  currentValue={95}
                  maxValue={100}
                  unlocked={false}
                />
                <BadgeProgress 
                  badgeType="long-term"
                  badgeName="Loyalty Expert"
                  description="Work with at least 5 clients for more than 6 months each."
                  currentValue={5}
                  maxValue={5}
                  unlocked={true}
                />
                <BadgeProgress 
                  badgeType="quality-champion"
                  badgeName="Zero Defect Master"
                  description="Deliver 10 consecutive projects with zero quality issues."
                  currentValue={8}
                  maxValue={10}
                  unlocked={false}
                />
              </div>
            </div> */}

            {/* Badge Tabs for Different Categories */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <Tabs
                defaultValue="all"
                value={badgeTab}
                onValueChange={setBadgeTab}
              >
                <TabsList className="flex w-full bg-gray-50 border-b border-gray-200 p-0 h-12">
                  {/* <TabsTrigger 
                    value="all" 
                    className="flex-1 h-full rounded-none data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-blue-500 data-[state=active]:shadow-none"
                  >
                    <div className="flex items-center gap-1.5">
                      <Award className="h-4 w-4" />
                      <span>All Badges</span>
                    </div>
                  </TabsTrigger> */}
                  <TabsTrigger
                    value="achievements"
                    className="flex-1 h-full rounded-none data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-purple-500 data-[state=active]:shadow-none"
                  >
                    <div className="flex items-center gap-1.5">
                      <Trophy className="h-4 w-4" />
                      <span>Achievements</span>
                    </div>
                  </TabsTrigger>
                  <TabsTrigger
                    value="certifications"
                    className="flex-1 h-full rounded-none data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:shadow-none"
                  >
                    <div className="flex items-center gap-1.5">
                      <Star className="h-4 w-4" />
                      <span>Certifications</span>
                    </div>
                  </TabsTrigger>
                  {/* <TabsTrigger 
                    value="client" 
                    className="flex-1 h-full rounded-none data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-rose-500 data-[state=active]:shadow-none"
                  >
                    <div className="flex items-center gap-1.5">
                      <Heart className="h-4 w-4" />
                      <span>Client Thanks</span>
                    </div>
                  </TabsTrigger> */}
                </TabsList>

                <div className="p-4">
                  {/* <TabsContent value="all" className="mt-0 p-0">
                    <BadgeCollection 
                      badges={allBadges} 
                      title="All Earned Badges & Achievements" 
                      className="bg-gradient-to-r from-gray-50 to-blue-50/30 p-4 rounded-lg border border-gray-100"
                      maxDisplay={12}
                    />
                  </TabsContent> */}

                  <TabsContent value="achievements" className="mt-0 p-0">
                    {isEditing ? (
                      <div className="mb-4 flex gap-2">
                        <Input
                          value={newAchievement}
                          onChange={(e) => setNewAchievement(e.target.value)}
                          placeholder="Enter new achievement"
                          className="flex-1"
                        />
                        <Button
                          onClick={handleAddAchievement}
                          disabled={!newAchievement.trim()}
                          className="bg-purple-500 text-white hover:bg-purple-600"
                        >
                          Add
                        </Button>
                        <Button
                          onClick={() => setIsEditing(false)}
                          className="bg-purple-500 text-white hover:bg-purple-600"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : achievementBadges.length <= 0 && !freelancerId ? (
                      <Button
                        onClick={() => setIsEditing(true)}
                        className="bg-purple-500 text-white hover:bg-purple-600 mb-2"
                      >
                        Add achievement
                      </Button>
                    ) : !freelancerId && (
                      <Button
                        onClick={() => setIsEditing(true)}
                        className="bg-purple-500 text-white hover:bg-purple-600 mb-2"
                      >
                        Edit achievement
                      </Button>
                    )}
                    <BadgeCollection
                      badges={achievementBadges}
                      title="Career Achievements"
                      className="bg-gradient-to-r from-purple-50/50 to-blue-50/50 p-4 rounded-lg border border-purple-100"
                      emptyMessage="No achievements added yet"
                      isEditing={isEditing}
                      onRemove={handleRemoveAchievement}
                    />
                  </TabsContent>

                  <TabsContent value="certifications" className="mt-0 p-0">
                    {isEditing ? (
                      <div className="mb-4 flex gap-2">
                        <Input
                          value={newCertification}
                          onChange={(e) => setNewCertification(e.target.value)}
                          placeholder="Enter new certificate"
                          className="flex-1"
                        />
                        <Button
                          onClick={handleAddCertification}
                          disabled={!newCertification.trim()}
                          className="bg-purple-500 text-white hover:bg-purple-600"
                        >
                          Add
                        </Button>
                        <Button
                          onClick={() => setIsEditing(false)}
                          className="bg-purple-500 text-white hover:bg-purple-600"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : certificationBadges.length <= 0 && !freelancerId  ? (
                      <Button
                        onClick={() => setIsEditing(true)}
                        className="bg-purple-500 text-white hover:bg-purple-600 mb-2"
                      >
                        Add certificate
                      </Button>
                    ) : ( !freelancerId &&
                      <Button
                        onClick={() => setIsEditing(true)}
                        className="bg-purple-500 text-white hover:bg-purple-600 mb-2"
                      >
                        Edit certificate
                      </Button>
                    )}
                    <BadgeCollection
                      badges={certificationBadges}
                      title="Professional Certifications"
                      className="bg-gradient-to-r from-amber-50/50 to-orange-50/50 p-4 rounded-lg border border-amber-100"
                      emptyMessage="No certificates added yet"
                      isEditing={isEditing}
                      onRemove={handleRemoveCertification}
                    />
                  </TabsContent>

                  {/* <TabsContent value="client" className="mt-0 p-0">
                    <BadgeCollection 
                      badges={clientBadges} 
                      title="Client Appreciation Badges" 
                      className="bg-gradient-to-r from-rose-50/50 to-pink-50/50 p-4 rounded-lg border border-rose-100"
                      emptyMessage="No client appreciation badges yet"
                    />
                  </TabsContent> */}
                </div>
              </Tabs>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
